import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Copy, Check, Trash2 } from 'lucide-react';
import Modal from '../common/Modal';
import FileTypeIcon from './FileTypeIcon';
import { InlineSpinner } from '../common/LoadingState';
import sharingService from '../../services/sharingService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import useDebounce from '../../hooks/useDebounce';
import { grantableLevels, shortLevel, P } from '../../utils/permissions';
import { formatDate } from '../../utils/format';
import cn from '../../utils/cn';

/** The three permission cards the v2 share dialog offers (intersected with what the sharer holds). */
const CARDS = [
  { key: 'view', label: 'View', desc: 'Open and preview only — no download.' },
  { key: 'download', label: 'Download', desc: 'Preview and download a copy.' },
  { key: 'edit', label: 'Edit', desc: 'Rename, tag and upload new versions.' },
];

const EXPIRY = [
  { key: '1', label: '24 hours' },
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
];

/** What stands between the link and the document. Exactly one of these, never two. */
const PROTECTION = [
  { key: 'password', label: 'Password' },
  { key: 'email', label: 'Email code' },
  { key: 'none', label: 'Link only' },
];

/** How an existing link is described in the list, including ones made before the chooser. */
function describeGate(link) {
  const emails = link.allowedEmails || [];
  const named = link.accessType === 'emails';
  const recipients = emails.length === 1 ? emails[0] : `${emails.length} named recipients`;

  if (named && link.hasPassword) return `Password, then an email code to ${recipients}`;
  if (named) return `Email code to ${recipients}`;
  if (link.hasPassword) return 'Password';
  return 'Anyone with the link';
}

/* ---------------------------------------------------------------- internal */

function InternalTab({ document: doc, onClose, onChanged }) {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 200);
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState([]);
  const [level, setLevel] = useState('view');
  const [message, setMessage] = useState('');
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const levels = useMemo(() => grantableLevels(doc.capabilities || []), [doc.capabilities]);
  const allowed = new Set(levels.map((l) => l.key));
  const cards = CARDS.filter((c) => allowed.has(c.key === 'edit' ? 'replace' : c.key));

  const loadShares = useCallback(async () => {
    setLoading(true);
    try {
      setShares(await sharingService.listShares(doc.id));
    } finally {
      setLoading(false);
    }
  }, [doc.id]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  useEffect(() => {
    let cancelled = false;
    sharingService.searchPrincipals(debounced).then((list) => {
      if (!cancelled) setSuggestions(list.filter((s) => !picked.some((p) => p.id === s.id)).slice(0, 6));
    });
    return () => {
      cancelled = true;
    };
  }, [debounced, picked]);

  const submit = async () => {
    if (!picked.length) return;
    setBusy(true);
    setError(null);
    try {
      const caps = level === 'edit' ? ['view', 'download', 'edit', 'replace'] : level === 'download' ? ['view', 'download'] : ['view'];
      await sharingService.share(doc.id, {
        principals: picked.map((p) => ({ type: p.type, id: p.id })),
        capabilities: caps,
        message: message.trim(),
      });
      toast.success(`Shared with ${picked.length === 1 ? picked[0].name : `${picked.length} recipients`}`);
      onChanged?.();
      onClose();
    } catch (err) {
      setError(err.message || 'The document could not be shared.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (share) => {
    await sharingService.revokeShare(doc.id, share.id);
    toast.success(`Access removed for ${share.principal?.name}`);
    onChanged?.();
    loadShares();
  };

  if (!levels.length) {
    return <div className="callout">You can open this document but cannot re-share it. Ask {doc.owner?.name || 'the owner'} for access.</div>;
  }

  return (
    <>
      <div className="stack" style={{ gap: 14 }}>
        <div>
          <label className="lbl" htmlFor="share-target">
            User or department
          </label>
          <input
            id="share-target"
            className="in"
            placeholder="Start typing a name or department"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {picked.map((p) => (
              <span key={p.id} className="pill pill-accent">
                {p.name}
                <button type="button" onClick={() => setPicked((l) => l.filter((x) => x.id !== p.id))} aria-label={`Remove ${p.name}`} style={{ display: 'grid', marginLeft: 2 }}>
                  <X size={11} />
                </button>
              </span>
            ))}
            {suggestions.map((s) => (
              <button
                key={`${s.type}-${s.id}`}
                type="button"
                className="pill"
                style={{ cursor: 'pointer', border: 0 }}
                onClick={() => {
                  setPicked((l) => [...l, s]);
                  setQuery('');
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="lbl">Permission</label>
          <div style={{ display: 'grid', gap: 8 }}>
            {cards.map((c) => (
              <label key={c.key} className="rad">
                <input type="radio" name="perm" checked={level === c.key} onChange={() => setLevel(c.key)} />
                <span className="dot" />
                <span>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{c.label}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {c.desc}
                  </div>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="lbl" htmlFor="share-msg">
            Message (optional)
          </label>
          <textarea id="share-msg" className="in" placeholder="Context for the recipient" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} />
        </div>

        {!loading && shares.length > 0 && (
          <div>
            <label className="lbl">Already has access</label>
            <div className="panel" style={{ overflow: 'hidden' }}>
              {shares.map((s) => (
                <div key={s.id} className="side-list__item" style={{ borderTop: 'none', borderBottom: '1px solid var(--row-rule)' }}>
                  <span className="row" style={{ gap: 8, minWidth: 0 }}>
                    <span className="itile">{(s.principal?.name || '?').slice(0, 1).toUpperCase()}</span>
                    <span className="truncate">{s.principal?.name}</span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    <span className="pill pill-info">{shortLevel(s.capabilities)}</span>
                    <button type="button" className="btn--icon" style={{ width: 28, height: 28 }} onClick={() => revoke(s)} aria-label="Remove access">
                      <Trash2 size={14} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {loading && <InlineSpinner label="Loading current access…" />}
        {error && <div className="banner banner--danger">{error}</div>}
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-g" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-p" onClick={submit} disabled={!picked.length || busy}>
          {busy ? 'Sharing…' : 'Share'}
        </button>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- external */

function ExternalTab({ document: doc, onClose, onChanged }) {
  const toast = useToast();
  const [expiry, setExpiry] = useState('7');
  const [protection, setProtection] = useState('password');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [existing, setExisting] = useState([]);

  const loadLinks = useCallback(async () => {
    try {
      setExisting(await sharingService.listLinks(doc.id));
    } catch {
      setExisting([]);
    }
  }, [doc.id]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const revoke = async (l) => {
    await sharingService.revokeLink(doc.id, l.id);
    toast.success('Link revoked — it stops working immediately');
    if (link?.id === l.id) setLink(null);
    onChanged?.();
    loadLinks();
  };

  const url = link ? link.url || `${window.location.origin}/s/${link.token}` : '';

  const generate = async () => {
    if (link) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Link copied');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Could not copy — select the link and copy it manually.');
      }
      return;
    }
    if (protection === 'password' && password.trim().length < 6) {
      setError('That password is too short — use at least 6 characters.');
      return;
    }
    if (protection === 'email' && !email.trim()) {
      setError('Enter the address that may open this link.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await sharingService.createLink(doc.id, {
        // Whichever gate was chosen, and only that one.
        accessType: protection === 'email' ? 'emails' : 'anyone',
        allowedEmails: protection === 'email' ? [email.trim()] : [],
        capabilities: ['view'],
        expiryDays: Number(expiry),
        password: protection === 'password' ? password.trim() : null,
      });
      setLink(created);
      onChanged?.();
      loadLinks();
      toast.success('Secure link generated');
    } catch (err) {
      setError(err.message || 'The link could not be created.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="stack" style={{ gap: 14 }}>
        <div className="callout">External links are HTTPS, time-limited, logged in the audit trail and revocable at any time.</div>

        {doc.isConfidential && <div className="banner banner--warning">This document is marked confidential and cannot be shared externally.</div>}

        <div>
          <label className="lbl">Expires</label>
          <div className="seg" style={{ width: '100%' }}>
            {EXPIRY.map((e) => (
              <button key={e.key} type="button" className={cn(expiry === e.key && 'is-active')} style={{ flex: 1 }} onClick={() => setExpiry(e.key)} disabled={Boolean(link)}>
                {e.label}
              </button>
            ))}
          </div>
        </div>

        {/* One gate, chosen on purpose.

            These used to be two independent fields, and filling in a recipient's address
            quietly added a second gate on top of the password — so a link made with both
            asked for the password, then for a code, which is not what anybody typing an
            address into a box called "Recipient email" was trying to arrange. Which one
            protects the link is now the question being asked, and it has one answer. */}
        <div>
          <label className="lbl">How the recipient opens it</label>
          <div className="seg" style={{ width: '100%' }}>
            {PROTECTION.map((p) => (
              <button key={p.key} type="button" className={cn(protection === p.key && 'is-active')} style={{ flex: 1 }} onClick={() => setProtection(p.key)} disabled={Boolean(link)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {protection === 'password' && (
          <div>
            <label className="lbl" htmlFor="link-pw">Password</label>
            <input id="link-pw" className="in" type="text" placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} disabled={Boolean(link)} minLength={6} />
            {/* Said before the attempt, not after it. The server enforces the same six. */}
            <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
              {password.trim() && password.trim().length < 6
                ? 'Too short — use at least 6 characters.'
                : 'Tell them the password some other way — not in the message carrying the link.'}
            </div>
          </div>
        )}

        {protection === 'email' && (
          <div>
            <label className="lbl" htmlFor="link-email">Recipient email</label>
            <input id="link-email" className="in" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={Boolean(link)} />
            <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
              Only this address can open it: they enter it on the page and we email them a code.
              Sending the link is still up to you — we do not send it for you.
            </div>
          </div>
        )}

        {protection === 'none' && (
          <div className="muted" style={{ fontSize: 11.5 }}>
            Anyone holding the link can open it, including anyone it gets forwarded to. Worth
            keeping short-lived.
          </div>
        )}

        {link && (
          <div className="screen">
            <label className="lbl">Secure link · expires {formatDate(link.expiresAt)}</label>
            <input className="in" readOnly value={url} onFocus={(e) => e.target.select()} style={{ fontSize: 12.5, background: 'var(--surface-2)' }} />
          </div>
        )}

        {existing.length > 0 && (
          <div>
            <label className="lbl">Active links</label>
            <div className="panel" style={{ overflow: 'hidden' }}>
              {existing.map((l) => (
                <div key={l.id} className="side-list__item" style={{ borderTop: 'none', borderBottom: '1px solid var(--row-rule)' }}>
                  <span className="stack" style={{ gap: 1, minWidth: 0 }}>
                    {/* What actually opens it, in the same words the chooser above uses.
                        Older links may carry both gates; those are described as they are
                        rather than tidied into the new shape, which would be a lie. */}
                    <span style={{ fontSize: 13 }}>
                      {describeGate(l)}
                    </span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      Expires {formatDate(l.expiresAt)} · {l.downloadCount || 0} opens
                    </span>
                  </span>
                  <span className="row" style={{ gap: 2 }}>
                    {l.token && (
                      <button
                        type="button"
                        className="btn--icon"
                        style={{ width: 28, height: 28 }}
                        aria-label="Copy link"
                        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/s/${l.token}`).then(() => toast.success('Link copied'))}
                      >
                        <Copy size={14} />
                      </button>
                    )}
                    <button type="button" className="btn--icon" style={{ width: 28, height: 28 }} aria-label="Revoke link" onClick={() => revoke(l)}>
                      <Trash2 size={14} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              The link itself is only shown once, when created. Revoking takes effect immediately.
            </div>
          </div>
        )}

        {error && <div className="banner banner--danger">{error}</div>}
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-g" onClick={onClose}>
          Close
        </button>
        <button type="button" className="btn btn-p" onClick={generate} disabled={busy || doc.isConfidential}>
          {busy ? 'Generating…' : link ? (copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy link</>) : 'Generate secure link'}
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- shell */

export default function ShareDialog({ open, onClose, document: doc, initialTab = 'people', onChanged }) {
  const [tab, setTab] = useState('internal');
  const { can, features } = useAuth();

  useEffect(() => {
    if (open) setTab(initialTab === 'link' ? 'external' : 'internal');
  }, [open, initialTab]);

  if (!doc) return null;

  const externalAllowed = can(P.DOCUMENT_SHARE_EXTERNAL) && features?.externalSharing;

  return (
    <Modal open={open} onClose={onClose} width={540}>
      <div className="stack" style={{ gap: 16, paddingTop: 22 }}>
        <div className="row" style={{ gap: 12 }}>
          <FileTypeIcon name={doc.name} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, overflowWrap: 'anywhere' }}>Share “{doc.name}”</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Stays in {doc.path || doc.department?.name}. Recipients get access, not a copy.
            </div>
          </div>
        </div>

        {externalAllowed && (
          <div className="seg" style={{ alignSelf: 'flex-start' }}>
            <button type="button" className={cn(tab === 'internal' && 'is-active')} onClick={() => setTab('internal')}>
              Internal
            </button>
            <button type="button" className={cn(tab === 'external' && 'is-active')} onClick={() => setTab('external')}>
              External link
            </button>
          </div>
        )}

        {tab === 'internal' ? (
          <InternalTab document={doc} onClose={onClose} onChanged={onChanged} />
        ) : (
          <ExternalTab document={doc} onClose={onClose} onChanged={onChanged} />
        )}
      </div>
    </Modal>
  );
}
